/**
 * What the `relationships` and `havens` sections share (owner, 2026-11-02: the
 * Safe-haven correlations gauges were split out of What moves together, and
 * both still read the one nightly derive, `/macro/relationships`).
 *
 * `useRelationshipsPayload` is the one fetch helper: two sections mounted
 * together share one request in flight rather than asking twice. Nothing is
 * kept once it settles — the next mount asks again, as each section did alone.
 */

import { useEffect, useState, type CSSProperties } from "react";
import { fetchRelationships, MacroNotReady, type RelationshipsPayload } from "../../api/macro-relationships";

export type Load<T> = { state: "loading" } | { state: "ready"; data: T } | { state: "error"; message: string };

export const errText = (e: unknown) =>
  e instanceof MacroNotReady ? e.message : e instanceof Error ? e.message : "The relationships data could not be loaded.";

let inFlight: Promise<RelationshipsPayload> | null = null;

/** `fetchRelationships`, shared while a request is in flight. */
function sharedFetch(): Promise<RelationshipsPayload> {
  if (!inFlight) {
    const p = fetchRelationships();
    inFlight = p;
    const clear = () => {
      if (inFlight === p) inFlight = null;
    };
    p.then(clear, clear);
  }
  return inFlight;
}

/** The derive's payload for a section, loading → ready | error. */
export function useRelationshipsPayload(): Load<RelationshipsPayload> {
  const [load, setLoad] = useState<Load<RelationshipsPayload>>({ state: "loading" });
  useEffect(() => {
    let live = true;
    sharedFetch()
      .then((data) => live && setLoad({ state: "ready", data }))
      .catch((e) => live && setLoad({ state: "error", message: errText(e) }));
    return () => {
      live = false;
    };
  }, []);
  return load;
}

// -- controls --------------------------------------------------------------------

export function Seg<T extends string | number>({
  label, value, options, onPick, disabled,
}: {
  label: string;
  value: T;
  options: Array<{ v: T; text: string }>;
  onPick: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={S.lbl}>{label}</span>
      <span role="group" aria-label={label} style={{ ...S.seg, opacity: disabled ? 0.5 : 1 }}>
        {options.map((o) => {
          const on = o.v === value;
          return (
            <button
              key={String(o.v)}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => onPick(o.v)}
              style={on ? S.segOn : S.segBtn}
            >
              {o.text}
            </button>
          );
        })}
      </span>
    </span>
  );
}

/** A polyline that lifts the pen over a null rather than drawing through it. */
export function pathOf(s: ReadonlyArray<number | null>, X: (i: number) => number, Y: (v: number) => number): string {
  let d = "", pen = false;
  s.forEach((v, i) => {
    if (v == null) {
      pen = false;
      return;
    }
    d += `${pen ? "L" : "M"}${X(i).toFixed(1)},${Y(v).toFixed(1)}`;
    pen = true;
  });
  return d;
}

// -- styles ----------------------------------------------------------------------

/** The app's own font (Inter), not a monospace (owner, 2026-11-02). */
const mono = "inherit";
const pill: CSSProperties = {
  fontFamily: mono, fontSize: 11, padding: "1px 7px", borderRadius: 99, border: "1px solid", whiteSpace: "nowrap",
};

/** Named by key, so the export's type names no csstype path (TS2742). */
type StyleKey =
  | "root"
  | "head"
  | "title"
  | "lbl"
  | "seg"
  | "segBtn"
  | "segOn"
  | "dim"
  | "mono"
  | "note"
  | "body"
  | "cmp"
  | "box"
  | "boxTitle"
  | "hhh"
  | "read"
  | "pillOk"
  | "pillBad"
  | "pillNone";

const styles: Record<StyleKey, CSSProperties> = {
  root: {
    width: "100%", height: "100%", overflow: "auto", background: "var(--qf-surface)", color: "var(--qf-text)",
    fontSize: 14, display: "flex", flexDirection: "column",
  },
  head: {
    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "7px 12px",
    background: "var(--qf-surface-alt)", borderBottom: "1px solid var(--qf-border)",
  },
  title: { fontSize: 14, marginRight: 6 },
  lbl: { fontSize: 11.5, color: "var(--qf-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" },
  seg: { display: "inline-flex", border: "1px solid var(--qf-border)", borderRadius: 5, overflow: "hidden" },
  segBtn: {
    padding: "3px 9px", fontFamily: mono, fontSize: 11.5, background: "transparent", color: "var(--qf-text-secondary)",
    border: 0, cursor: "pointer",
  },
  segOn: {
    padding: "3px 9px", fontFamily: mono, fontSize: 11.5, background: "var(--qf-selection)", color: "var(--qf-text)",
    border: 0, cursor: "pointer", fontWeight: 600,
  },
  dim: { color: "var(--qf-text-muted)", fontSize: 12 },
  mono: { fontFamily: mono, fontSize: 12.5 },
  note: { padding: "12px", color: "var(--qf-text-secondary)", fontSize: 13.5 },
  body: { display: "flex", flexDirection: "column", gap: 12, padding: 12 },
  cmp: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 12 },
  box: { background: "var(--qf-background)", border: "1px solid var(--qf-border)", borderRadius: 6, overflow: "hidden" },
  boxTitle: {
    padding: "7px 12px", fontSize: 11.5, letterSpacing: "0.08em", textTransform: "uppercase",
    color: "var(--qf-text-secondary)", borderBottom: "1px solid var(--qf-border)",
  },
  hhh: { display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", padding: "8px 12px 0", fontSize: 13.5 },
  read: { padding: "2px 12px 4px", fontSize: 13 },
  pillOk: { ...pill, color: "var(--qf-positive)" },
  pillBad: { ...pill, color: "var(--qf-negative)" },
  pillNone: { ...pill, color: "var(--qf-text-muted)" },
};

export const S: Readonly<Record<StyleKey, CSSProperties>> = styles;
